import { usePlayDetailStore } from '@/pages/playDetail/store';

/**
 * 获取下一集的结果类型
 */
export interface NextEpisodeResult {
  /** 是否有下一集 */
  hasNext: boolean;
  /** 下一集的 vid */
  nextVid?: string;
  /** 下一集的 cid（如果有变化） */
  nextCid?: string;
  /** 数据来源：'store' 表示从 playDetailStore 获取，'playlist' 表示从播放列表获取 */
  source?: 'store' | 'playlist';
}

/**
 * 通用方法：获取下一集信息
 * 优先使用 playDetailStore 的 getNextVid 方法，备选使用播放列表查找
 *
 * @param cid 当前 cid
 * @param vid 当前 vid
 * @param videoListInfo 播放列表信息（可选，作为备选方案）
 * @returns NextEpisodeResult 包含是否有下一集以及下一集的信息
 */
export function getNextEpisode(cid: string | null, vid: string | null, videoListInfo?: any): NextEpisodeResult {
  // 参数校验
  if (!cid || !vid) {
    return { hasNext: false };
  }

  // 方法1: 优先使用 getNextVid 方法（来自 playDetailStore）
  try {
    const { getNextVid } = usePlayDetailStore.getState();
    const nextVidFromStore = getNextVid(cid, vid);

    if (nextVidFromStore) {
      return {
        hasNext: true,
        nextVid: nextVidFromStore,
        nextCid: cid, // cid 保持不变
        source: 'store',
      };
    }
  } catch (error) {
    console.warn('[nextEpisodeUtils] 使用 getNextVid 获取下一集失败:', error);
  }

  // 方法2: 备选方案 - 使用播放列表查找
  if (videoListInfo?.play_list_info && Array.isArray(videoListInfo.play_list_info)) {
    const playList = videoListInfo.play_list_info;
    const currentIndex = playList.findIndex((video: any) => video.vid === vid);

    if (currentIndex !== -1 && currentIndex < playList.length - 1) {
      // 有下一集，返回下一集信息
      const nextVideo = playList[currentIndex + 1];
      const nextVid = nextVideo.vid;
      const nextCid = nextVideo.cid || cid; // 如果下一集有 cid 则使用，否则使用当前 cid

      return {
        hasNext: true,
        nextVid,
        nextCid,
        source: 'playlist',
      };
    }
  }

  // 两种方法都没有找到下一集
  return { hasNext: false };
}

/**
 * 检查是否有下一集
 * 这是 getNextEpisode 的简化版本，只返回布尔值
 *
 * @param cid 当前 cid
 * @param vid 当前 vid
 * @param videoListInfo 播放列表信息（可选，作为备选方案）
 * @returns 是否有下一集
 */
export function hasNextEpisode(cid: string | null, vid: string | null, videoListInfo?: any): boolean {
  const result = getNextEpisode(cid, vid, videoListInfo);
  return result.hasNext;
}
